import ChevronRightOutlined from "@mui/icons-material/ChevronRightOutlined";
import { Box, Typography } from "@mui/material";
import {
  NO_TRANSITION,
  sectionSx,
  TEXT,
} from "@/features/reassistRequests/utils/tokens";
import { type SaleDocumentSummary } from "../types";
import {
  displayName,
  formatBuildingFeatures,
  formatPrice,
  showsAddressLine,
} from "../utils/format";
import { SaleBuildingTypeChip } from "./SaleBuildingTypeChip";
import { SalePurchaseChip } from "./SalePurchaseChip";

interface Props {
  document: SaleDocumentSummary;
  onSelect: (id: number) => void;
}

/** 入れ物の幅がこれ以上のとき、横に並べる配置へ切り替える。 */
const WIDE = "@container (min-width: 480px)";

const LabeledValue: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Typography sx={{ ...TEXT.meta, color: "text.secondary" }}>
    {label}{" "}
    <Box component="span" sx={{ color: "text.primary" }}>
      {value || "-"}
    </Box>
  </Typography>
);

/**
 * 販売中の物件1件。購入前に見せる項目（マンション名・住所・地番・家屋番号、建物の種類・階数・構造・用途）と価格だけを表示する。
 * /reassist の依頼カードと同じ白地、1px の枠、角丸8、影なし。hover とフォーカスで枠を primary.main にする。
 */
export const SaleDocumentCard: React.FC<Props> = ({ document, onSelect }) => {
  // 登記と突き合わせて物件を見分けるための補足（階数・構造・用途）。建物の種類は見出しの隣のタグで出す
  const features = formatBuildingFeatures(document, [
    "floors",
    "structure",
    "use",
  ]);
  return (
    <Box
      component="li"
      data-testid="sale-document-card"
      sx={{ listStyle: "none", containerType: "inline-size" }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => {
          onSelect(document.id);
        }}
        sx={(theme) => ({
          ...sectionSx(theme),
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          width: "100%",
          textAlign: "left",
          cursor: "pointer",
          p: 2,
          color: "text.primary",
          font: "inherit",
          transition: "border-color 0.2s",
          ...NO_TRANSITION,
          "&:hover": { borderColor: theme.palette.primary.main },
          "&:focus-visible": {
            borderColor: theme.palette.primary.main,
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: "2px",
          },
          [WIDE]: {
            px: 3,
            py: 2.5,
            gridTemplateColumns: "minmax(0, 1fr) auto 20px",
            columnGap: 2,
            alignItems: "center",
          },
        })}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 1,
              rowGap: 0.5,
            }}
          >
            <Typography
              component="h3"
              sx={{ ...TEXT.cardTitle, overflowWrap: "anywhere" }}
            >
              {displayName(document)}
            </Typography>
            <SalePurchaseChip
              fileCount={document.fileCount}
              purchasedCount={document.purchasedCount}
            />
            <SaleBuildingTypeChip buildingType={document.buildingType} />
          </Box>
          {/* 見出しが住所と同じ（マンション名が無い物件）ときは、同じ文字を2回出さない */}
          {showsAddressLine(document) && (
            <Typography
              sx={{
                ...TEXT.body,
                mt: 0.5,
                color: "text.primary",
                overflowWrap: "anywhere",
              }}
            >
              {document.address}
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              columnGap: 2,
              mt: showsAddressLine(document) ? 0 : 0.5,
            }}
          >
            <LabeledValue label="地番" value={document.chiban} />
            <LabeledValue label="家屋番号" value={document.kaokuNumber} />
          </Box>
          {features !== "" && (
            <Typography
              sx={{
                ...TEXT.meta,
                color: "text.secondary",
                overflowWrap: "anywhere",
              }}
            >
              {features}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            mt: 1.5,
            fontVariantNumeric: "tabular-nums",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            columnGap: 1,
            [WIDE]: {
              mt: 0,
              flexDirection: "column",
              alignItems: "flex-end",
            },
          }}
        >
          {document.price === null ? (
            <Typography sx={{ ...TEXT.body, color: "text.secondary" }}>
              {formatPrice(null)}
            </Typography>
          ) : (
            <>
              <Typography sx={{ ...TEXT.meta, color: "text.secondary" }}>
                1PDFあたり
              </Typography>
              <Typography sx={{ ...TEXT.cardTitle }}>
                {formatPrice(document.price)}
              </Typography>
            </>
          )}
        </Box>
        <ChevronRightOutlined
          aria-hidden
          sx={{
            display: "none",
            color: "text.secondary",
            fontSize: 20,
            [WIDE]: { display: "block" },
          }}
        />
      </Box>
    </Box>
  );
};
